<template>
  <ContentSection spacing="none" class="pb-16 transition-[height] sm:pb-28">
    <!-- FILTER -->
    <CleaningMethodsFilter
      v-model="filter"
      :fields-of-application="fieldsOfApplicationList"
    />

    <Container use-full-width class="pt-18">
      <!-- METHODS -->
      <TransitionGroup
        tag="ul"
        name="list"
        class="
          flex flex-col gap-x-default gap-y-default
          sm:grid sm:grid-cols-[repeat(auto-fill,_minmax(20rem,_1fr))]
        "
      >
        <li v-for="(cleaningMethod, methodIndex) in filteredMethods" :key="cleaningMethod.id ?? methodIndex">
          <!-- <pre>{{ cleaningMethod }}</pre> -->
          <CardCleaningMethod :data="cleaningMethod || []" />
        </li>
      </TransitionGroup>

      <!-- NO RESULTS -->
      <Transition name="fade-in">
        <NoEntriesFound v-if="!filteredMethods?.length" />
      </Transition>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsOverviewCleaningMethodsFragment, CleaningMethodFragment } from '#gql'

// on automatic mode data.entries are added by [statamic-repo]/app/Fieldtypes/Components.php
const props = defineProps<{
  data: Enforce<MainComponentsOverviewCleaningMethodsFragment>
  index: number
}>()

const { t } = useI18n()
const route = useRoute()

// Filter
export type CleaningMethodFilterType = {
  field_of_application: string | null
  searchTerm: string
}
const filter = ref<CleaningMethodFilterType>({
  field_of_application: route.query.field ? (route.query.field as string) : 'all',
  searchTerm: route.query.search ? (route.query.search as string) : '',
})

const fieldsOfApplicationList = computed(() => { // list of fields of application to populate filter
  return [
    {
      id: 'all',
      slug: 'all',
      title: t('all'),
    },
    ...props.data.fields_of_application.map(field_of_application => ({
      id: field_of_application.id,
      slug: field_of_application.slug,
      title: field_of_application.title,
    })),
  ]
})

// Filtering
const filteredMethods = computed(() => {
  let filterResult = props.data.entries

  // NO FILTERING NEEDED
  if (filter.value.field_of_application === 'all' && filter.value.searchTerm === '') {
    return filterResult
  }

  // FIELD OF APPLICATION
  if (filter.value.field_of_application !== 'all') {
    filterResult = filterResult.filter((method: CleaningMethodFragment) => method.fields_of_application.slug === filter.value.field_of_application)
  }

  // SEARCH
  filterResult = filterResult.filter((method: CleaningMethodFragment) => method.title.toLowerCase().includes(filter.value.searchTerm.toLowerCase()))

  return filterResult
})

watch(
  () => filter.value,
  () => {
    saveFilterInUrl()
  },
  { deep: true },
)

// Saves the filter in the URL
const router = useRouter()
function saveFilterInUrl() {
  router.push({
    query: {
      field: filter.value.field_of_application,
      search: filter.value.searchTerm,
    },
  })
}
</script>
